import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import { simpleDashDate } from 'lib/date';
import CoordinatePatientsIcon from 'svg/CoordinatePatientsIcon';
import XIcon2 from 'svg/XIcon2';

import { COORDINATE_REQUEST_GENDERS } from './constants';

export type CoordinateRequestFormValues = {
  name: string;
  dateOfBirth: Date | null;
  sex: { label: string; value: string } | null;
};

const EMPTY_VALUES: CoordinateRequestFormValues = {
  name: '',
  dateOfBirth: null,
  sex: null,
};

type Props = {
  onSearch: (params: { name: string; dateOfBirth: string; sex: string }) => void;
  isLoading?: boolean;
};

function CoordinateRequestSearchForm({ onSearch, isLoading = false }: Props) {
  const [values, setValues] = useState<CoordinateRequestFormValues>(EMPTY_VALUES);

  const isComplete = !!values.name.trim() && !!values.dateOfBirth && !!values.sex && !isLoading;
  const hasAnyValue = !!values.name || !!values.dateOfBirth || !!values.sex;

  const handleSubmit = () => {
    if (!isComplete) return;
    onSearch({
      name: values.name.trim(),
      dateOfBirth: simpleDashDate(values.dateOfBirth) ?? '',
      sex: values.sex!.value,
    });
  };

  const handleClearAll = () => setValues(EMPTY_VALUES);

  return (
    <Container>
      <HeadlineWrapper>
        <CoordinatePatientsIcon />
        <Headline>Coordinate</Headline>
      </HeadlineWrapper>
      <SubHeadline>Search for a patient to initiate coordination with one of your partners in Olio</SubHeadline>
      <Fields>
        <InputGroup title='Patient Name'>
          <NameInputWrapper>
            <Input
              id='coordinate-request-name'
              data-testid='coordinate-request-name'
              placeholder='Patient Name'
              value={values.name}
              onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
            />
            {values.name && (
              <ClearButton
                type='button'
                aria-label='Clear patient name'
                onClick={() => setValues((v) => ({ ...v, name: '' }))}>
                <XIcon2 width={12} height={12} />
              </ClearButton>
            )}
          </NameInputWrapper>
        </InputGroup>
        <InlineRow>
          <InputGroup title='Date of Birth'>
            <DatePicker
              selected={values.dateOfBirth}
              maxDate={new Date()}
              onChange={(date) => setValues((v) => ({ ...v, dateOfBirth: date }))}
              showMonthDropdown
              showYearDropdown
              dropdownMode='select'
              $fullWidth
            />
          </InputGroup>
          <InputGroup title='Gender'>
            <Select
              inputId='coordinate-request-gender'
              placeholder='Gender'
              name='sex'
              value={values.sex}
              onChange={(value) => setValues((v) => ({ ...v, sex: value }))}
              options={COORDINATE_REQUEST_GENDERS}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
            />
          </InputGroup>
          <ClearAllRow $visible={hasAnyValue}>
            <ClearAllLink type='button' data-testid='coordinate-request-clear-all' onClick={handleClearAll}>
              Clear all fields
            </ClearAllLink>
          </ClearAllRow>
        </InlineRow>
        <SearchButton
          data-testid='coordinate-request-search-button'
          onClick={handleSubmit}
          disabled={!isComplete}
          loading={isLoading}
          fullWidth>
          Search
        </SearchButton>
      </Fields>
    </Container>
  );
}

export default CoordinateRequestSearchForm;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 540px;
`;

const HeadlineWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding-top: 24px;
  padding-bottom: 12px;
`;

const Headline = styled.h1`
  font-size: 24px;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.black100};
  margin: 0;
`;

const SubHeadline = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.black100};
  margin: 0;
  padding-bottom: 24px;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  text-align: left;
`;

const InlineRow = styled.div`
  display: flex;
  gap: 16px;
  position: relative;
  margin-bottom: 30px;

  & > * {
    flex: 1;
  }
`;

const SearchButton = styled(Button)``;

const NameInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.black25};
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.black100};
  }

  &:focus {
    outline: none;
    color: ${({ theme }) => theme.colors.black100};
  }
`;

const ClearAllRow = styled.div<{ $visible: boolean }>`
  position: absolute;
  right: 0;
  top: calc(100% - 18px);
  display: flex;
  justify-content: flex-end;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`;

const ClearAllLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.blue100};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: none;
    text-decoration: underline;
  }
`;
